import { Component, Prop, h, Host } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Cookie consent banner for SignNow.
 *
 * Each text is its own prop, so all of them — including the cookie-policy link inside the
 * description — are editable in the visual editor. The legacy `values` / `constants` objects
 * still work and act as the fallback.
 *
 * @product signnow
 * @version 1.1.0
 * @category SignNow
 * @contentConfig GDPR cookie consent banner with configurable text
 * @example
 *   <sn-cookie-type-1
 *     description='We use cookies to improve security and personalize the user experience. Click <a href="/cookies" title="Cookie Policy">here</a> to read our Cookie Policy. By clicking "Accept" you agree to the use of cookies.'
 *     accept-text="Accept"
 *     read-more-text="Read more"
 *     read-less-text="Read less"
 *   ></sn-cookie-type-1>
 */
@Component({
  tag: 'sn-cookie-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnCookieType1 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Text constants for the cookie banner (legacy prop name — prefer `values`).
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * Consent banner body text. HTML is allowed, e.g. the link to the cookie policy.
   * @content
   * @dynamic
   * @default "We use cookies to improve security, personalize the user experience, enhance our marketing activities (including cooperating with our 3rd party partners) and for other business use. Click <a href=\"/cookies\" title=\"Cookie Policy\">here</a> to read our Cookie Policy. By clicking \"Accept\" you agree to the use of cookies."
   */
  @Prop({ attribute: 'description' }) description?: string;

  /**
   * Accept button label.
   * @content
   * @dynamic
   * @default "Accept"
   */
  @Prop({ attribute: 'accept-text' }) acceptText?: string;

  /**
   * Label of the toggle that expands the truncated description.
   * @content
   * @dynamic
   * @default "Read more"
   */
  @Prop({ attribute: 'read-more-text' }) readMoreText?: string;

  /**
   * Label of the toggle that collapses the description again.
   * @content
   * @dynamic
   * @default "Read less"
   */
  @Prop({ attribute: 'read-less-text' }) readLessText?: string;

  /**
   * Legacy combined payload, kept as the fallback for pages authored against v1. The individual
   * text props above win when set — prefer them, since the editor renders an object prop as a
   * single raw-JSON field where anything but valid JSON is silently dropped.
   * @dynamic
   * @type {object}
   * @property {string} COOKIE_BANNER_DESCRIPTION - Cookie consent description text (HTML allowed, e.g. links to cookie policy)
   * @property {string} COOKIE_BANNER_ACCEPT - Accept button label
   * @property {string} COOKIE_BANNER_READ_MORE - "Read more" toggle label
   * @property {string} COOKIE_BANNER_READ_LESS - "Read less" toggle label
   * @required COOKIE_BANNER_DESCRIPTION
   */
  @Prop({ mutable: true }) values?: Record<string, string> | string;

  @Prop() delay?: number;
  @Prop() closeLabel?: string;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
    this.values = parseJsonProp(this.values);
  }

  render() {
    // Merge values into constants so both prop names work; the individual text props win last.
    const merged: Record<string, string> = {
      ...(this.constants as Record<string, string> | undefined),
      ...(this.values as Record<string, string> | undefined),
      ...(this.description ? { COOKIE_BANNER_DESCRIPTION: this.description } : {}),
      ...(this.acceptText ? { COOKIE_BANNER_ACCEPT: this.acceptText } : {}),
      ...(this.readMoreText ? { COOKIE_BANNER_READ_MORE: this.readMoreText } : {}),
      ...(this.readLessText ? { COOKIE_BANNER_READ_LESS: this.readLessText } : {}),
    };
    return (
      <Host data-color-mode={this.colorMode}>
        <cookie-type-1
          constants={Object.keys(merged).length ? merged : undefined}
          delay={this.delay}
          closeLabel={this.closeLabel}
        ></cookie-type-1>
      </Host>
    );
  }
}
